// Application constants - 120 lines ✅
import type { D365Version, IssueCategory, IssuePriority, KBCategory, ExpertiseDomain } from '@/types/database'

/**
 * Application ID for this app on the Kaycoo Agentic Platform.
 * Used to filter agents assigned to this application (app_id field).
 * Can be overridden from Settings page (stored in localStorage).
 */
export const APPLICATION_ID = localStorage.getItem('application_id') || import.meta.env.VITE_APPLICATION_ID || 'd365-ai-assistant'

/**
 * Kaycoo Agentic Platform API URL — must be set via VITE_CLAUDE_CD_API_URL.
 * Fail fast if missing rather than falling back to a stale hardcoded host.
 */
const _claudeCdApiUrl = import.meta.env.VITE_CLAUDE_CD_API_URL
if (!_claudeCdApiUrl) {
  throw new Error('VITE_CLAUDE_CD_API_URL is not set — configure it in .env or the deployment environment')
}
export const CLAUDE_CD_API_URL: string = _claudeCdApiUrl

export const D365_VERSIONS: D365Version[] = [
  '10.0.37', '10.0.38', '10.0.39', '10.0.40',
  '10.0.41', '10.0.42', '10.0.43', '10.0.44',
  '10.0.45', '10.0.46',
]

export const D365_PRODUCTS = ['Finance', 'Supply Chain Management', 'Commerce', 'Human Resources', 'Project Operations'] as const

export const D365_DEPLOYMENT_TYPES = ['Cloud', 'On-Premises', 'Cloud-Hosted Dev/Test'] as const

export const D365_MODULES = [
  'General Ledger',
  'Accounts Payable',
  'Accounts Receivable',
  'Cash and Bank Management',
  'Fixed Assets',
  'Budgeting',
  'Cost Accounting',
  'Inventory Management',
  'Warehouse Management',
  'Transportation Management',
  'Procurement',
  'Production Control',
  'Master Planning',
  'Product Information Management',
  'Sales and Marketing',
  'Project Management',
  'Human Resources',
  'Payroll',
  'Retail / Commerce',
  'Organization Administration',
  'System Administration',
  'Other',
] as const

export const ISSUE_CATEGORIES: { value: IssueCategory; label: string }[] = [
  { value: 'bug', label: 'Bug' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'configuration', label: 'Configuration' },
  { value: 'performance', label: 'Performance' },
  { value: 'data', label: 'Data Issue' },
  { value: 'integration', label: 'Integration' },
  { value: 'other', label: 'Other' },
]

export const ISSUE_PRIORITIES: { value: IssuePriority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
]

export const KB_CATEGORIES: { value: KBCategory; label: string }[] = [
  { value: 'bug_fix', label: 'Bug Fix' },
  { value: 'configuration', label: 'Configuration' },
  { value: 'best_practice', label: 'Best Practice' },
  { value: 'workaround', label: 'Workaround' },
  { value: 'integration', label: 'Integration' },
  { value: 'performance', label: 'Performance' },
  { value: 'migration', label: 'Migration / Upgrade' },
  { value: 'other', label: 'Other' },
]

export const EXPERTISE_DOMAINS: { value: ExpertiseDomain; label: string; description: string }[] = [
  { value: 'functional', label: 'Functional / Application', description: 'Business processes, workflows, forms, module-specific' },
  { value: 'technical', label: 'Technical / Development', description: 'X++, C#, extensions, data entities, Power Platform, SSRS reports' },
  { value: 'infrastructure', label: 'Infrastructure / LCS', description: 'Lifecycle Services, Azure, environments, deployment, DB' },
  { value: 'integration', label: 'Integration', description: 'OData, Data entities, Dual-write, Virtual entities, Power Automate' },
  { value: 'data', label: 'Data & Migration', description: 'Data management framework, BYOD, entity store, data packages' },
  { value: 'security', label: 'Security & Access', description: 'Security roles, duties, privileges, SoD, legal entities' },
  { value: 'configuration', label: 'Configuration', description: 'Feature management, parameters, number sequences, system config' },
]

export const CONFIDENCE_THRESHOLDS = {
  HIGH: 0.85,
  MEDIUM: 0.70,
  LOW: 0.0,
} as const

export const SEARCH_DEFAULTS = {
  MAX_RESULTS: 10,
  CONFIDENCE_THRESHOLD: 0.70,
} as const

export const AI_MODELS = {
  CHAT: 'gpt-4o-mini',
  EMBEDDING: 'text-embedding-3-small',
  FALLBACK: 'gpt-4o',
} as const

export const APPROVAL_STATUSES = {
  PENDING: 'pending',
  UNDER_REVIEW: 'under_review',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  CHANGES_REQUESTED: 'changes_requested',
} as const

export const ASSIGNMENT_STATUSES = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  REJECTED: 'rejected',
} as const

export const ROUTES = {
  HOME: '/',
  DASHBOARD: '/dashboard',
  ISSUES: '/issues',
  ISSUE_NEW: '/issues/new',
  ISSUE_DETAIL: '/issues/:id',
  KNOWLEDGE_BASE: '/knowledge',
  KNOWLEDGE_DETAIL: '/knowledge/:id',
  DOCUMENTS: '/documents',
  SETTINGS: '/settings',
  LOGIN: '/login',
} as const
